import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Save, Plus, RotateCcw, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Sprint 12 — AI Report Configuration is a PREVIEW SURFACE ONLY.
// The legacy `/api/ai-config*` endpoints only mutated an in-memory STORE
// (silent data loss on restart) and have been retired. Persistent AI-config
// CRUD is tracked as a follow-up. For now this page renders a seeded preview
// of the dimensions/thresholds the runtime AI eval actually uses, drawn from
// `services/videobot_eval.py` and `services/mcq_eval.py` so HR can review
// them.
const SEED = {
  dimensions: [
    { name: 'Clarity', weight: 20, source_primary: 'VideoBot', source_secondary: null, source_weight: [100] },
    { name: 'Depth', weight: 25, source_primary: 'Panel', source_secondary: 'VideoBot', source_weight: [70, 30] },
    { name: 'Structure', weight: 15, source_primary: 'Panel', source_secondary: null, source_weight: [100] },
    { name: 'Energy', weight: 15, source_primary: 'VideoBot', source_secondary: null, source_weight: [100] },
    { name: 'Concepts', weight: 25, source_primary: 'MCQ', source_secondary: 'Panel', source_weight: [60, 40] },
  ],
  recommendation_thresholds: [
    { label: 'Strong Recommend', min: 4.0, colour: 'green' },
    { label: 'Recommend', min: 3.2, colour: 'blue' },
    { label: 'Neutral', min: 2.5, colour: 'amber' },
    { label: 'Not Recommended', min: 0.0, colour: 'rose' },
  ],
};

export default function AiReportConfig() {
  const [dims, setDims] = useState(SEED.dimensions);
  const [addOpen, setAddOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [form, setForm] = useState({ name: '', weight: 10, source_primary: 'Panel', source_secondary: '' });

  const totalWeight = dims.reduce((a, d) => a + (d.weight || 0), 0);

  const addDim = () => {
    if (!form.name) { toast.error('Dimension name required'); return; }
    setDims([...dims, { ...form, source_secondary: form.source_secondary || null, source_weight: [100] }]);
    toast.success(`Dimension "${form.name}" added (preview only — not persisted).`);
    setAddOpen(false);
    setForm({ name: '', weight: 10, source_primary: 'Panel', source_secondary: '' });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Report Configuration"
        subtitle="Spider dimensions, weights, scoring scale, and per-dimension source weights for the AI evaluation engine."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-1.5" onClick={() => setResetOpen(true)} data-testid="reset-ai-config-button"><RotateCcw className="h-4 w-4" /> Reset</Button>
            <Button variant="outline" className="gap-1.5" onClick={() => setAddOpen(true)} data-testid="add-dimension-button"><Plus className="h-4 w-4" /> Add dimension</Button>
            <Button className="gap-1.5" onClick={() => toast.info('Saving will be available soon.')} data-testid="save-ai-config-button"><Save className="h-4 w-4" /> Save</Button>
          </div>
        }
      />

      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="flex items-start gap-3 p-4 text-xs text-amber-900" data-testid="ai-config-preview-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Preview only</p>
            <p className="mt-0.5 text-amber-800">
              Changes you make here aren’t saved yet. Full editing will be available soon.
              The values shown below are the current live scoring settings used by the AI
              evaluators.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated"><CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Spider dimensions ({dims.length})</p>
            <p className="text-xs text-muted-foreground">Each dimension scored on a 1–5 scale. Weights below sum to 100% across the overall evaluation.</p>
          </div>
          <div className={`rounded-full border px-3 py-1 text-xs font-semibold ${totalWeight === 100 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>Total weight: {totalWeight}%</div>
        </div>
        <div className="mt-4 space-y-4">
          {dims.map((d, i) => (
            <div key={d.name} className="rounded-xl border bg-card p-4" data-testid="ai-dimension-row">
              <div className="flex items-start justify-between">
                <div><p className="text-sm font-semibold">{d.name}</p><p className="text-xs text-muted-foreground">Primary: {d.source_primary === 'MCQ' ? 'IQ Assessment' : d.source_primary}{d.source_secondary && ` · Secondary: ${d.source_secondary === 'MCQ' ? 'IQ Assessment' : d.source_secondary}`} · Source weight: {d.source_weight.join(' / ')}</p></div>
                <div className="text-right"><p className="text-xs text-muted-foreground">Weight</p><p className="text-lg font-semibold tabular-nums">{d.weight}%</p></div>
              </div>
              <Slider min={0} max={30} step={1} value={[d.weight]} onValueChange={(v) => { const next = [...dims]; next[i] = { ...d, weight: v[0] }; setDims(next); }} className="mt-3" />
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Card className="card-elevated"><CardContent className="p-6">
        <p className="text-sm font-semibold">Recommendation thresholds</p>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
          {SEED.recommendation_thresholds.map((t) => (
            <div key={t.label} className="rounded-xl border bg-card p-4">
              <div className={`h-2 w-12 rounded-full ${t.colour === 'green' ? 'bg-emerald-500' : t.colour === 'blue' ? 'bg-sky-500' : t.colour === 'amber' ? 'bg-amber-500' : 'bg-rose-500'}`} />
              <p className="mt-2 text-sm font-semibold">{t.label}</p>
              <p className="text-xs text-muted-foreground">Score ≥ {t.min.toFixed(1)}</p>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-dimension-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Add spider dimension</DialogTitle>
            <DialogDescription>Dimensions appear on the candidate&apos;s AI report spider chart and contribute to the overall score.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-medium text-muted-foreground">Dimension name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. System Design" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Primary source</label>
                <Select value={form.source_primary} onValueChange={(v) => setForm({ ...form, source_primary: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Panel">Panel transcript</SelectItem><SelectItem value="VideoBot">Video Bot</SelectItem><SelectItem value="MCQ">IQ Assessment</SelectItem><SelectItem value="Code">Code editor</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Secondary (optional)</label>
                <Select value={form.source_secondary || 'none'} onValueChange={(v) => setForm({ ...form, source_secondary: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem><SelectItem value="Panel">Panel transcript</SelectItem><SelectItem value="VideoBot">Video Bot</SelectItem><SelectItem value="MCQ">IQ Assessment</SelectItem><SelectItem value="Code">Code editor</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground">Initial weight: {form.weight}%</label><Slider min={1} max={30} step={1} value={[form.weight]} onValueChange={(v) => setForm({ ...form, weight: v[0] })} className="mt-2" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addDim} data-testid="add-dimension-confirm">Add dimension</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-ai-config-dialog">
          <DialogHeader>
            <DialogTitle>Reset AI configuration?</DialogTitle>
            <DialogDescription>This restores the seeded dimensions and thresholds for the preview. (No backend persistence yet.)</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => { setDims(SEED.dimensions); toast.success('Preview reset to seeded values.'); setResetOpen(false); }}>Reset to defaults</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
